import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Clock, Phone, CheckCircle2 } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { company } from "../mock";

const WHEN = [
  "You need a scheduled break to rest, work or travel",
  "A family caregiver has an appointment or emergency",
  "You're recovering and can't provide care temporarily",
  "You want to prevent caregiver burnout before it starts",
];

const FAQS = [
  { q: "What is respite care?", a: "Respite care is short-term relief for family caregivers. A trained caregiver steps in — for a few hours, a day, or on a recurring schedule — so the primary caregiver can rest, work or handle other responsibilities while their loved one stays safe at home." },
  { q: "How is respite care paid for in Ohio?", a: "Respite may be covered under Ohio DODD waivers (community or residential respite), some MyCare Ohio plans, or arranged privately with no waiver required. We verify eligibility and authorization with your plan before services begin, and private-pay respite is available where staffing allows." },
  { q: "Can I schedule respite on a recurring basis?", a: "Yes. Many families schedule regular weekly respite so the primary caregiver gets a dependable break. One-time and short-notice requests are also welcome — availability depends on staffing and location." },
  { q: "How quickly can respite care start?", a: "Contact us at (614) 526-1548. Timing depends on your county, schedule and, for waiver-funded respite, authorization from your plan or county board. Private-pay respite can often begin sooner." },
];

export default function RespiteCare() {
  useSeo({
    title: "Respite Care in Ohio for Family Caregivers | Caregivers Alliance Homecare",
    description: "Respite care in Ohio: short-term relief for family caregivers. DODD community & residential respite, and private-pay respite with no waiver required. Now accepting new clients. Call (614) 526-1548.",
    keywords: "respite care Ohio, respite care for family caregivers Columbus, DODD respite provider Ohio, in home respite care near me, private pay respite Ohio",
    path: "/respite-care",
  });
  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-14">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            <Clock className="h-4 w-4" /> Relief for family caregivers
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl max-w-3xl">Respite Care in Ohio</h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            Caring for a loved one is a full-time commitment. Our respite caregivers give you a dependable
            break while your loved one stays safe and supported at home.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/contact"><Button data-testid="respite-request-btn" className="rounded-full bg-white text-[#0E5A72] hover:bg-white/90 h-12 px-7 font-semibold">REQUEST RESPITE CARE</Button></Link>
            <a href={`tel:${company.phoneRaw}`}><Button className="rounded-full bg-[#2E9E6A] hover:bg-[#23875A] text-white h-12 px-7 font-semibold"><Phone className="mr-2 h-4 w-4" /> {company.phone}</Button></a>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-display font-bold text-3xl text-[#0E2A33]">When families use respite</h2>
            <div className="mt-6 space-y-3">
              {WHEN.map((s) => (
                <div key={s} className="flex items-center gap-3 bg-[#F7FBFC] rounded-xl border border-slate-100 p-4">
                  <CheckCircle2 className="h-5 w-5 text-[#2E9E6A] shrink-0" />
                  <span className="text-slate-700 font-medium">{s}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#F7FBFC] rounded-2xl border border-slate-100 p-8">
            <h3 className="font-display font-bold text-xl text-[#0E2A33]">Ways to access respite</h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-600">
              <li>• <span className="font-semibold text-[#0E2A33]">DODD waivers:</span> community respite and residential respite may be authorized through your service plan and county board.</li>
              <li>• <span className="font-semibold text-[#0E2A33]">MyCare Ohio & waiver plans:</span> some plans include in-home respite — we verify your coverage before care begins.</li>
              <li>• <span className="font-semibold text-[#0E2A33]">Private pay:</span> flexible respite with no waiver required, where staffing allows.</li>
            </ul>
            <div className="mt-6 flex flex-col gap-3">
              <Link to="/mycare"><Button className="w-full rounded-full bg-[#0E5A72] hover:bg-[#0A4557] text-white h-12 font-semibold">VERIFY MY COVERAGE</Button></Link>
              <Link to="/private-pay"><Button variant="outline" className="w-full rounded-full border-[#0E5A72] text-[#0E5A72] h-12 font-semibold">Private-pay respite options</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-[#F7FBFC]">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0E2A33] text-center">Respite Care — Frequently Asked Questions</h2>
          <div className="mt-8 space-y-4">
            {FAQS.map((f) => (
              <div key={f.q} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <div className="font-semibold text-[#0E2A33]">{f.q}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8 text-sm text-slate-600">
            Related: <Link to="/hpc" className="text-[#0E5A72] font-medium hover:underline">HPC Services</Link> ·{" "}
            <Link to="/family-caregiver" className="text-[#0E5A72] font-medium hover:underline">Family Caregiver Options</Link> ·{" "}
            <Link to="/how-to-get-home-care-in-ohio" className="text-[#0E5A72] font-medium hover:underline">How to Get Home Care in Ohio</Link>
          </div>
        </div>
      </section>
    </>
  );
}
